import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { readConsent, saveConsent } from "@/lib/consent";

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const choice = readConsent();
    setVisible(!choice);
    setAnalytics(choice?.analytics ?? false);
    setMarketing(choice?.marketing ?? false);
    const open = () => {
      const saved = readConsent();
      setAnalytics(saved?.analytics ?? false);
      setMarketing(saved?.marketing ?? false);
      setManaging(true);
    };
    window.addEventListener("pe:cookie-settings", open);
    return () => window.removeEventListener("pe:cookie-settings", open);
  }, []);

  function choose(allowAnalytics: boolean, allowMarketing: boolean) {
    saveConsent(allowAnalytics, allowMarketing);
    setAnalytics(allowAnalytics);
    setMarketing(allowMarketing);
    setVisible(false);
    setManaging(false);
  }

  return <>
    {visible && !managing && (
      <section role="dialog" aria-label="Cookie preferences" aria-describedby="pe-cookie-description" className="fixed inset-x-0 bottom-0 z-[120] border-t border-border bg-background p-5 shadow-lg sm:p-6">
        <div className="container-page flex flex-col gap-5 px-0 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <h2 className="font-serif text-2xl">Your cookie choices</h2>
            <p id="pe-cookie-description" className="mt-2 text-sm leading-relaxed text-muted-foreground">Necessary cookies keep the site working. Optional analytics help measure visits and enquiries; marketing cookies support advertising. You can accept, reject or choose your preferences.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button className="text-cream hover:bg-charcoal" onClick={() => choose(true, true)}>Accept all</Button>
            <Button variant="outline" onClick={() => choose(false, false)}>Reject all</Button>
            <Button variant="outline" onClick={() => setManaging(true)}>Manage</Button>
          </div>
        </div>
      </section>
    )}
    <Dialog.Root open={managing} onOpenChange={setManaging}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[130] bg-charcoal/60" />
        <Dialog.Content aria-label="Cookie settings" className="fixed left-1/2 top-1/2 z-[131] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-md border border-border bg-background p-6 shadow-lg max-h-[85dvh] overflow-y-auto">
          <Dialog.Title className="pr-10 font-serif text-2xl">Cookie settings</Dialog.Title>
          <Dialog.Description className="mt-3 text-sm leading-relaxed text-muted-foreground">Choose which optional cookies to allow. You can change your choices at any time.</Dialog.Description>
          <div className="my-6 divide-y divide-border">
            <label className="flex items-center justify-between gap-4 py-4" htmlFor="pe-necessary"><span>Strictly necessary</span><Switch id="pe-necessary" aria-label="Strictly necessary" checked disabled /></label>
            <label className="flex items-center justify-between gap-4 py-4" htmlFor="pe-analytics"><span>Analytics</span><Switch id="pe-analytics" aria-label="Analytics" checked={analytics} onCheckedChange={setAnalytics} /></label>
            <label className="flex items-center justify-between gap-4 py-4" htmlFor="pe-marketing"><span>Marketing</span><Switch id="pe-marketing" aria-label="Marketing" checked={marketing} onCheckedChange={setMarketing} /></label>
          </div>
          <Button className="w-full text-cream hover:bg-charcoal" onClick={() => choose(analytics, marketing)}>Save choices</Button>
          <Dialog.Close asChild><Button variant="ghost" size="icon" aria-label="Close cookie settings" className="absolute right-3 top-3"><X /></Button></Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  </>;
}